// src/components/PushPrompt.jsx
// Soft-ask opt-in for browser push notifications. Best practice: never prompt cold -
// only request permission from this explicit user action, with context.
import React, { useState } from "react";
import {
  pushSupported,
  notificationPermission,
  isIOS,
  isStandalonePWA,
  syncBrowserPushSubscription,
} from "../utils/browserPush";

function dismissKey(userId) {
  return `gc:push-prompt-dismissed:${userId || "anon"}`;
}

export default function PushPrompt({ token, prefEnabled, userId }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(userId)) === "1";
    } catch {
      return false;
    }
  });
  const [permission, setPermission] = useState(notificationPermission());
  const [busy, setBusy] = useState(false);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissKey(userId), "1");
    } catch {}
  };

  // The user turned browser notifications off in their profile, or already dealt with this.
  if (dismissed || !prefEnabled) return null;

  const iosTab = isIOS() && !isStandalonePWA();

  // iOS only delivers web push to an installed PWA; in a Safari tab the prompt can't work,
  // so guide the user to install instead.
  if (iosTab) {
    return (
      <div className="mb-3 flex items-start gap-3 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-3 py-2.5">
        <i className="fi fi-rr-bell not-italic text-blue-700 dark:text-blue-300 text-lg leading-none mt-0.5" />
        <div className="flex-1 text-sm text-blue-950 dark:text-blue-200">
          <div className="font-semibold">Get notified of new messages</div>
          <div className="mt-0.5 text-blue-900/80 dark:text-blue-200/80">
            On iPhone/iPad, tap the Share icon and choose <b>Add to Home Screen</b>, then open
            GearCheckAuto from your home screen to turn on notifications.
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-blue-700 dark:text-blue-300 hover:text-blue-900 text-sm"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  // Only offer the prompt when push is supported and the user hasn't decided yet.
  if (!pushSupported() || permission !== "default") return null;

  const enable = async () => {
    setBusy(true);
    try {
      const res = await syncBrowserPushSubscription(token, true, { promptIfNeeded: true });
      setPermission(res.permission);
      // Whether they allowed (subscribed) or blocked, we're done asking - hide the card.
      if (res.permission !== "default") dismiss();
    } catch {
      // leave the card up so they can retry
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-3 flex items-start gap-3 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-3 py-2.5">
      <i className="fi fi-rr-bell not-italic text-blue-700 dark:text-blue-300 text-lg leading-none mt-0.5" />
      <div className="flex-1 text-sm text-blue-950 dark:text-blue-200">
        <div className="font-semibold">Get notified of new messages</div>
        <div className="mt-0.5 text-blue-900/80 dark:text-blue-200/80">
          Turn on notifications to hear about replies and updates even when GearCheckAuto is closed.
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={enable}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? "Enabling…" : "Enable notifications"}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-lg px-3 py-1.5 text-sm text-blue-900 dark:text-blue-200 hover:bg-blue-100"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
